import { AppSidePanel } from "components/AppLayout/AppSidePanel";
import { ChatPane } from "components/ChatPane/ChatPane";
import { CommentsSlider } from "components/CommentsSlider/CommentsSlider";
import { AnimatedSlide } from "components/design/AnimatedSlide";
import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import { ResizablePanel } from "components/DesignSystem/ResizablePanel/ResizablePanel";
import Tab from "components/DesignSystem/Tab/Tab";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { ExpandNavIcon } from "components/icons/ExpandNavIcon";
import { COMMENT_TAB_OPENED } from "constants/analyticsEvents";
import { useAnalytics } from "hooks/useAnalytics";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useUpdateQuery } from "hooks/useQuery";
import { Dispatch, SetStateAction, memo } from "react";
import { useDrop } from "react-dnd";
import uploadCloud from "static/images/uploadCloud.svg";
import { COMMENT_TYPE, NoteResponse } from "store/apis/notes";
import { setOpenComment } from "store/slices/openComment";
import { setTransactionsInChat } from "store/slices/transactionsInChat";
import { Transaction } from "types/Models/books";
import { useIsSmallScreen } from "hooks/useIsSmallScreen";
import { Cross } from "components/icons/Cross";

type TransactionsSidePanelProps = {
  open: boolean;
  onToggle: () => void;
  channelId: string | null;
  setChannelId: Dispatch<SetStateAction<string | null>>;
  filter?: object;
};

const MemoizedChatPane = memo(ChatPane);

export const TransactionsSidePanel = ({
  open,
  onToggle,
  channelId,
  setChannelId,
  filter,
}: TransactionsSidePanelProps) => {
  const dispatch = useAppDispatch();
  const entityId = useCurrentEntityId();
  const { trackEvent } = useAnalytics();
  const { update } = useUpdateQuery();

  const openComment = useAppSelector((state) => state.openComment.openComment);
  const allFilters = useAppSelector(
    (state) => state.transactionCardPreview.filtersApplied
  );

  const onDrop = (transaction: string) => {
    const txn: Transaction = JSON.parse(transaction);
    const transactionsToSendtoChat = {
      id: txn.uuid,
      amount: txn.amount,
      source: txn.source,
      date: txn.date,
      vendor: {
        logo: txn.logo,
        name: txn.merchant,
      },
      isCreditCard: txn.is_credit_card,
    };

    dispatch(
      setTransactionsInChat({
        transactionsInChat: [transactionsToSendtoChat],
        entityId: entityId,
      })
    );
  };

  const [{ isOver }, drop] = useDrop(() => ({
    accept: "transaction",
    drop: ({ transaction }: { transaction: string }) => onDrop(transaction),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  const ref = (node: HTMLDivElement | null) => {
    drop(node);
  };

  const setActiveComment = (comment: NoteResponse) => {
    if (comment.transaction?.uuid) {
      update({
        query: "selected_transaction_id",
        value: comment.transaction?.uuid,
      });
    }

    if (comment.transaction) {
      dispatch(setOpenComment(comment.transaction.uuid));
    }
  };

  const handleCommentsTabClick = () => {
    trackEvent(COMMENT_TAB_OPENED, { type: "TRANSACTION_COMMENT" });
  };

  const isSmallScreen = useIsSmallScreen();

  const panel = (
    <Tab.Root defaultValue="CHAT" asChild>
      <DashboardContainer className="t-h-full t-w-full t-shrink-0 t-grow-1 t-border-0 t-border-l t-border-solid t-border-neutral-0">
        <DashboardContainer.Header>
          <Tab.List className="t-min-h-14 t-items-end t-px-4">
            <Tab.Trigger value="CHAT">Chat</Tab.Trigger>
            <Tab.Trigger onClick={handleCommentsTabClick} value="COMMENTS">
              Comments
            </Tab.Trigger>
            <div className="t-ml-auto t-self-center t-pr-3">
              <ToolTip text={open ? "Hide Chat" : "Show Chat"} side="bottom">
                <Button customType="ghost_icon" size="small" onClick={onToggle}>
                  {isSmallScreen ? (
                    <Cross />
                  ) : (
                    <ExpandNavIcon className="t-text-text-30" />
                  )}
                </Button>
              </ToolTip>
            </div>
          </Tab.List>
        </DashboardContainer.Header>
        <DashboardContainer.Content>
          <Tab.Content value="CHAT" asChild>
            <div ref={ref} className="t-h-full">
              {isOver && (
                <div className="t-absolute t-z-10 t-flex t-h-[-webkit-fill-available] t-w-[-webkit-fill-available] t-flex-col t-items-center t-justify-center t-gap-3 t-border t-border-dashed t-border-neutral-70 t-bg-[#f3f3f580] t-h-full t-w-[-moz-available]">
                  <img
                    src={uploadCloud}
                    alt="upload"
                    className="t-animate-drag-and-drop"
                  />
                  <div>Drag and drop here</div>
                </div>
              )}
              {open && (
                <MemoizedChatPane
                  setChannelId={setChannelId}
                  channelId={channelId}
                  disableAutoFocus
                  filter={filter}
                />
              )}
            </div>
          </Tab.Content>
          <Tab.Content value="COMMENTS" asChild>
            <div className="t-relative t-flex t-h-full t-flex-col t-gap-3 t-overflow-y-auto t-p-4 t-pt-0">
              <CommentsSlider
                activeComment={openComment}
                setActiveComment={setActiveComment}
                commentTypes={[COMMENT_TYPE.TRANSACTION_COMMENT]}
                transactionFilters={allFilters}
              />
            </div>
          </Tab.Content>
        </DashboardContainer.Content>
      </DashboardContainer>
    </Tab.Root>
  );

  if (isSmallScreen) {
    if (!open) return null;

    return (
      <AppSidePanel>
        <div className="t-fixed t-inset-0 t-z-slider t-bg-surface">{panel}</div>
      </AppSidePanel>
    );
  }

  return (
    <AppSidePanel>
      <AnimatedSlide
        isVisible={open}
        direction="right"
        className="t-relative t-h-full t-max-w-max t-shrink-0"
      >
        <ResizablePanel
          defaultSize={360}
          minSize={360}
          maxSize={720}
          handleDirection="left"
          resetOnOpen={open}
          onCollapse={onToggle}
          className="t-h-full"
        >
          {panel}
        </ResizablePanel>
      </AnimatedSlide>
    </AppSidePanel>
  );
};
